import React, { useMemo } from 'react';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import SwitchComponent from 'components/common/Switch/Switch';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisIntensity/criticalAnalysisIntensity.module.scss';
import { LineChart } from 'components/common/charts/lineChart/lineChart';
import { options as progressMeasurementOptions } from 'components/common/GlobalFilterComponent/components/progressMeasurementFilter/progressMeasurementFilter';
import { useScurveProvider } from 'components/widgets/SCurve/Scurve.provider';
import NoData from 'components/common/NoData/noData';
import { useBaseWidget } from 'hooks/useBaseWidget';
import { useScurveLogic } from 'components/widgets/SCurve/useScurve.logic';

const componentKey = 'criticalityWeightedSCurve';
const id = 'IntensityCriticalityWeight';

const Scurve = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: IComponentProps) => {
    // Provider
    const { ui, api } = useScurveProvider({
        componentKey,
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
    });

    const title = useMemo(() => {
        if (ui.state.progressMeasurement) {
            const label = progressMeasurementOptions.find((item) => item.value === ui.state.progressMeasurement)?.label;
            return `${label} S-Curve`;
        }
        return 'Criticality Weighted S-Curve';
    }, [ui.state.progressMeasurement]);

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    // Logic
    const { noDataCondition, options, isShowCriticalityAnnotation, update, series, notApplicable } = useScurveLogic({
        ui,
        api,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={title}
            tooltip={api.scurveGraph.data?.info}
            titleComponents={[
                <div data-testid={'annotation-filter'} className={classes.annotationFilter}>
                    {isShowCriticalityAnnotation ? 'Hide Annotations' : 'Show Annotations'}
                    <SwitchComponent
                        onChange={(event) => update({ isShowCriticalityAnnotation: event.target.checked })}
                        checked={isShowCriticalityAnnotation}
                    />
                </div>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : null,
            ]}
        >
            <LineChart
                series={series}
                options={options}
                noData={
                    noDataCondition ? (
                        <NoData>{notApplicable ? 'Not applicable with applied filter' : null}</NoData>
                    ) : undefined
                }
                isFetching={api.scurveGraph.isFetching}
                className={classes.lineChart}
            />
        </WidgetWithTitle>
    );
};

export default React.memo(Scurve);
